import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { BRAND } from "../lib/brand";
import { HOLIDAY_TOPICS, PLOT_TOPICS } from "../lib/content";

const MegaMenu = ({ items, basePath }) => (
  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[640px] bg-white border border-zinc-200 shadow-2xl rounded-2xl p-8 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
    <div className="grid grid-cols-2 gap-x-8 gap-y-3">
      {items.map((t) => (
        <Link
          key={t.slug}
          to={`${basePath}/${t.slug}`}
          data-testid={`mega-link-${t.slug}`}
          className="group/item block py-2"
        >
          <div className="text-[15px] font-medium text-zinc-900 group-hover/item:text-[#1D4ED8] transition-colors">
            {t.title}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5 line-clamp-1">{t.summary}</div>
        </Link>
      ))}
    </div>
    <div className="mt-6 pt-4 border-t border-zinc-100 flex items-center justify-between">
      <Link to={basePath} className="text-sm text-zinc-600 hover:text-[#1D4ED8] link-underline">
        View overview →
      </Link>
      <Link to="/clarity-call" className="text-sm text-[#1D4ED8] font-medium">
        Book a Clarity Call →
      </Link>
    </div>
  </div>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-zinc-200/70"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-[72px]">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <img src={BRAND.logoDark} alt="Zentry" className="h-9 object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <div className="group relative">
            <button className="flex items-center gap-1 text-sm text-zinc-800 hover:text-[#1D4ED8] transition-colors py-6" data-testid="nav-holiday">
              Holiday Homes <ChevronDown className="h-4 w-4" />
            </button>
            <MegaMenu items={HOLIDAY_TOPICS} basePath="/holiday-homes" />
          </div>

          <div className="group relative">
            <button className="flex items-center gap-1 text-sm text-zinc-800 hover:text-[#1D4ED8] transition-colors py-6" data-testid="nav-plots">
              Plots &amp; Land <ChevronDown className="h-4 w-4" />
            </button>
            <MegaMenu items={PLOT_TOPICS} basePath="/plots" />
          </div>

          <NavLink to="/zentry-build" className="text-sm text-zinc-800 hover:text-[#1D4ED8]" data-testid="nav-build">
            Zentry Build
          </NavLink>
          <NavLink to="/insights" className="text-sm text-zinc-800 hover:text-[#1D4ED8]" data-testid="nav-insights">
            Insights
          </NavLink>
          <NavLink to="/about" className="text-sm text-zinc-800 hover:text-[#1D4ED8]" data-testid="nav-about">
            About
          </NavLink>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link to="/clarity-call" className="text-sm text-zinc-800 hover:text-[#1D4ED8] link-underline" data-testid="nav-clarity-text">
            Book a clarity call
          </Link>
          <Link to="/clarity-call" className="btn-primary text-sm" data-testid="nav-cta-button">
            <Phone className="h-4 w-4" /> Talk to Zentry
          </Link>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2"
          aria-label="menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-zinc-200 bg-white" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3">
            <Link to="/holiday-homes" onClick={() => setOpen(false)} className="py-2 text-zinc-800">Holiday Homes</Link>
            <Link to="/plots" onClick={() => setOpen(false)} className="py-2 text-zinc-800">Plots &amp; Land</Link>
            <Link to="/zentry-build" onClick={() => setOpen(false)} className="py-2 text-zinc-800">Zentry Build</Link>
            <Link to="/insights" onClick={() => setOpen(false)} className="py-2 text-zinc-800">Insights</Link>
            <Link to="/about" onClick={() => setOpen(false)} className="py-2 text-zinc-800">About</Link>
            <Link to="/contact" onClick={() => setOpen(false)} className="py-2 text-zinc-800">Contact</Link>
            <Link to="/clarity-call" onClick={() => setOpen(false)} className="btn-primary justify-center mt-2">
              Book a Clarity Call
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
